import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Typography,
  Paper,
  Box,
  Link,
  Grid,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';

const ContentCard = styled(Paper)`
  padding: 1.5rem;
  margin-bottom: 1rem;
  background-color: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    box-shadow: 0 4px 6px rgba(0,0,0,0.07);
  }
`;

const StyledLink = styled(Link)`
  color: #e0301e !important;
  text-decoration: none !important;
  font-weight: 500;
  display: block;
  margin-bottom: 0.5rem;
  
  &:hover {
    text-decoration: underline !important;
  }
`;

const StatsContainer = styled(Box)`
  margin-bottom: 3rem;
`;

const CompanyStatsGrid = styled(Grid)`
  margin-bottom: 2rem;
`;

const CompanyStatCard = styled(Box)`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  transition: transform 0.2s ease-in-out;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

const CompanyLogo = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const ResultCount = styled(Typography)`
  font-size: 1.5rem;
  font-weight: 600;
  color: #e0301e;
`;

const CompanyName = styled(Typography)`
  font-weight: 500;
  color: #374151;
`;

const ResultsContainer = styled(Box)`
  margin-top: 2rem;
`;

const SectionTitle = styled(Typography)`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const SnippetText = styled(Typography)`
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.5;
`;

interface CompetitorResult {
  url: string;
  title: string;
  snippet?: string;
  date?: string | null;
  fileType?: string;
}

interface CompanyResults {
  items: CompetitorResult[];
  totalResults: number;
}

interface CompetitorSearchProps {
  searchQuery: string;
  filters: {
    startDate: Date | null;
    endDate: Date | null;
    contentTypes: string[];
    sortBy: 'date' | 'relevance';
    competitors: string[];
  };
}

const detectContentType = (title: string, snippet: string = ''): string | null => {
  const text = `${title} ${snippet}`.toLowerCase();
  
  // Report keywords
  if (text.match(/raport|report|survey|badanie|analiza|publikacja|insights|outlook|research|perspektywy|studium/)) {
    return 'raport';
  }
  
  // Podcast keywords
  if (text.match(/podcast|odcinek|audio|nagranie|słuchaj|posłuchaj/)) {
    return 'podcast';
  }
  
  // Event/Webinar keywords
  if (text.match(/webinar|webinarium|wydarzenie|konferencja|spotkanie|szkolenie|seminarium|warsztat|wirtualne|online|stacjonarne|hybrydowe/)) {
    return 'wydarzenie';
  }
  
  return null;
};

const filterResultsByContentType = (results: CompetitorResult[], contentTypes: string[]): CompetitorResult[] => {
  if (!contentTypes.length) return results;
  
  return results.filter(result => {
    const detectedType = detectContentType(result.title, result.snippet);
    return detectedType && contentTypes.includes(detectedType);
  });
};

const CompetitorSearch: React.FC<CompetitorSearchProps> = ({ searchQuery, filters }) => {
  const [results, setResults] = useState<Record<string, CompanyResults>>({
    deloitte: { items: [], totalResults: 0 },
    ey: { items: [], totalResults: 0 },
    pwc: { items: [], totalResults: 0 },
    mckinsey: { items: [], totalResults: 0 },
    kpmg: { items: [], totalResults: 0 },
    accenture: { items: [], totalResults: 0 }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const searchCompetitorSite = async (query: string, domain: string): Promise<CompanyResults> => {
      try {
        const apiKey = process.env.REACT_APP_GOOGLE_SEARCH_API_KEY;
        const searchEngineId = process.env.REACT_APP_GOOGLE_SEARCH_ENGINE_ID;

        if (!apiKey || !searchEngineId) {
          throw new Error('Google Search API credentials not configured');
        }

        const cleanQuery = query.trim().replace(/\s+/g, ' ');
        
        // Build search parameters
        const searchParams = new URLSearchParams({
          key: apiKey,
          cx: searchEngineId,
          q: cleanQuery,
          siteSearch: domain,
          siteSearchFilter: 'i',
          num: '10'
        });

        // Add date range if specified
        if (filters.startDate) {
          searchParams.append('dateRestrict', `d${Math.ceil((Date.now() - filters.startDate.getTime()) / (1000 * 60 * 60 * 24))}`);
        }

        // Add sort parameter
        if (filters.sortBy === 'date') {
          searchParams.append('sort', 'date');
        }

        // Add content type filters if specified
        if (filters.contentTypes.length > 0) {
          const typeQueries = filters.contentTypes.map(type => {
            switch (type) {
              case 'raport':
                return '(raport OR report)';
              case 'podcast':
                return 'podcast';
              case 'wydarzenie':
                return '(wydarzenie OR webinar OR webinarium)';
              default:
                return type;
            }
          });
          const typeQuery = typeQueries.join(' OR ');
          const currentQuery = searchParams.get('q') || cleanQuery;
          searchParams.set('q', `${currentQuery} (${typeQuery})`);
        }

        const searchUrl = `https://www.googleapis.com/customsearch/v1?${searchParams.toString()}`;
        const response = await fetch(searchUrl);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(`Google Search API error: ${data.error?.message || 'Unknown error'}`);
        }

        return {
          items: (data.items || []).map((item: any) => ({
            url: item.link,
            title: item.title,
            snippet: item.snippet,
            date: item.pagemap?.metatags?.[0]?.['article:published_time'] || 
                  item.pagemap?.metatags?.[0]?.['date'] || 
                  null,
            fileType: item.mime || item.fileFormat || 
                     (item.link.match(/\.([a-z]+)$/)?.[1] || '').toUpperCase()
          })),
          totalResults: parseInt(data.searchInformation?.totalResults) || 0
        };
      } catch (error) {
        console.error(`Error searching ${domain}:`, error);
        throw error;
      }
    };

    const fetchCompetitorResults = async () => {
      if (!searchQuery.trim()) {
        setResults({
          deloitte: { items: [], totalResults: 0 },
          ey: { items: [], totalResults: 0 },
          pwc: { items: [], totalResults: 0 },
          mckinsey: { items: [], totalResults: 0 },
          kpmg: { items: [], totalResults: 0 },
          accenture: { items: [], totalResults: 0 }
        });
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const searchPromises = [];
        
        if (filters.competitors.includes('deloitte')) {
          searchPromises.push(searchCompetitorSite(searchQuery, 'deloitte.com/pl'));
        }
        if (filters.competitors.includes('ey')) {
          searchPromises.push(searchCompetitorSite(searchQuery, 'ey.com/pl_pl'));
        }
        if (filters.competitors.includes('pwc')) {
          searchPromises.push(searchCompetitorSite(searchQuery, 'pwc.pl'));
          searchPromises.push(searchCompetitorSite(searchQuery, 'strategyand.pwc.com/pl/pl'));
        }
        if (filters.competitors.includes('mckinsey')) {
          searchPromises.push(searchCompetitorSite(searchQuery, 'www.mckinsey.com/pl'));
        }
        if (filters.competitors.includes('kpmg')) {
          searchPromises.push(searchCompetitorSite(searchQuery, 'kpmg.com/pl/pl'));
        }
        if (filters.competitors.includes('accenture')) {
          searchPromises.push(searchCompetitorSite(searchQuery, 'www.accenture.com/pl-pl'));
        }

        const results = await Promise.all(searchPromises);
        
        const newResults: Record<string, CompanyResults> = {
          deloitte: { items: [], totalResults: 0 },
          ey: { items: [], totalResults: 0 },
          pwc: { items: [], totalResults: 0 },
          mckinsey: { items: [], totalResults: 0 },
          kpmg: { items: [], totalResults: 0 },
          accenture: { items: [], totalResults: 0 }
        };

        let resultIndex = 0;
        if (filters.competitors.includes('deloitte')) {
          const deloitteResults = results[resultIndex++];
          newResults.deloitte = {
            items: filterResultsByContentType(deloitteResults.items, filters.contentTypes),
            totalResults: deloitteResults.totalResults
          };
        }
        if (filters.competitors.includes('ey')) {
          const eyResults = results[resultIndex++];
          newResults.ey = {
            items: filterResultsByContentType(eyResults.items, filters.contentTypes),
            totalResults: eyResults.totalResults
          };
        }
        if (filters.competitors.includes('pwc')) {
          const pwcResults = results[resultIndex++];
          const strategyResults = results[resultIndex++];
          const combinedResults = {
            items: [...(pwcResults?.items || []), ...(strategyResults?.items || [])]
              .sort((a, b) => {
                if (filters.sortBy === 'date') {
                  const dateA = a.date ? new Date(a.date).getTime() : 0;
                  const dateB = b.date ? new Date(b.date).getTime() : 0;
                  return dateB - dateA;
                }
                return 0;
              }),
            totalResults: (pwcResults?.totalResults || 0) + (strategyResults?.totalResults || 0)
          };
          newResults.pwc = {
            items: filterResultsByContentType(combinedResults.items, filters.contentTypes),
            totalResults: combinedResults.totalResults
          };
        }
        if (filters.competitors.includes('mckinsey')) {
          const mckinseyResults = results[resultIndex++];
          newResults.mckinsey = {
            items: filterResultsByContentType(mckinseyResults.items, filters.contentTypes),
            totalResults: mckinseyResults.totalResults
          };
        }
        if (filters.competitors.includes('kpmg')) {
          const kpmgResults = results[resultIndex++];
          newResults.kpmg = {
            items: filterResultsByContentType(kpmgResults.items, filters.contentTypes),
            totalResults: kpmgResults.totalResults
          };
        }
        if (filters.competitors.includes('accenture')) {
          const accentureResults = results[resultIndex++];
          newResults.accenture = {
            items: filterResultsByContentType(accentureResults.items, filters.contentTypes),
            totalResults: accentureResults.totalResults
          };
        }

        setResults(newResults);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An error occurred';
        console.error('Error fetching competitor results:', errorMessage);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompetitorResults();
  }, [searchQuery, filters]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" padding={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" gutterBottom>
        Error: {error}
      </Typography>
    );
  }

  const hasResults = Object.values(results).some(companyResults => companyResults.totalResults > 0);

  if (!hasResults) {
    return (
      <Typography color="textSecondary">
        No competitor content found for this topic.
      </Typography>
    );
  }

  const ResultCard = styled(Box)`
    padding: 16px;
    border-radius: 8px;
    background: #fff;
    margin-bottom: 16px;
    border: 1px solid #e5e7eb;
    transition: all 0.2s;
    
    &:hover {
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }
  `;

  const ResultDate = styled(Typography)`
    color: #4b5563;
    font-size: 0.875rem;
    margin-bottom: 8px;
    font-weight: 500;
    display: flex;
    align-items: center;
    padding: 4px 8px;
    background: #f3f4f6;
    border-radius: 4px;
    width: fit-content;
  `;

  const FileType = styled(Chip)`
    margin-left: 8px;
    height: 20px;
    font-size: 0.75rem;
    background: #e5e7eb;
    font-weight: 500;
  `;

  const FreshnessIndicator = styled(Box)<{ fresh?: boolean }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => props.fresh ? '#10B981' : '#9CA3AF'};
    margin-right: 8px;
    display: inline-block;
  `;

  return (
    <Box>
      <SectionTitle>Market Research</SectionTitle>
      
      <StatsContainer>
        <Typography variant="h6" sx={{ color: '#1f2937', marginBottom: 2 }}>
          Search Results Overview
        </Typography>
        <CompanyStatsGrid container spacing={3}>
          <Grid item xs={2}>
            <CompanyStatCard>
              <CompanyLogo src="/logos/deloitte.png" alt="Deloitte" />
              <Box>
                <CompanyName>Deloitte</CompanyName>
                <ResultCount>{results.deloitte.totalResults} results</ResultCount>
              </Box>
            </CompanyStatCard>
          </Grid>
          <Grid item xs={2}>
            <CompanyStatCard>
              <CompanyLogo src="/logos/ey.png" alt="EY" />
              <Box>
                <CompanyName>EY</CompanyName>
                <ResultCount>{results.ey.totalResults} results</ResultCount>
              </Box>
            </CompanyStatCard>
          </Grid>
          <Grid item xs={2}>
            <CompanyStatCard>
              <CompanyLogo src="/logos/pwc.png" alt="PwC" />
              <Box>
                <CompanyName>PwC</CompanyName>
                <ResultCount>{results.pwc.totalResults} results</ResultCount>
              </Box>
            </CompanyStatCard>
          </Grid>
          <Grid item xs={2}>
            <CompanyStatCard>
              <CompanyLogo src="/logos/mckinsey.png" alt="McKinsey" />
              <Box>
                <CompanyName>McKinsey</CompanyName>
                <ResultCount>{results.mckinsey.totalResults} results</ResultCount>
              </Box>
            </CompanyStatCard>
          </Grid>
          <Grid item xs={2}>
            <CompanyStatCard>
              <CompanyLogo src="/logos/kpmg.png" alt="KPMG" />
              <Box>
                <CompanyName>KPMG</CompanyName>
                <ResultCount>{results.kpmg.totalResults} results</ResultCount>
              </Box>
            </CompanyStatCard>
          </Grid>
          <Grid item xs={2}>
            <CompanyStatCard>
              <CompanyLogo src="/logos/accenture.png" alt="Accenture" />
              <Box>
                <CompanyName>Accenture</CompanyName>
                <ResultCount>{results.accenture.totalResults} results</ResultCount>
              </Box>
            </CompanyStatCard>
          </Grid>
        </CompanyStatsGrid>
      </StatsContainer>

      <ResultsContainer>
        <Typography variant="h6" sx={{ color: '#1f2937', marginBottom: 2 }}>
          Latest Publications
        </Typography>
        <Grid container spacing={3}>
          {Object.entries(results).map(([company, companyResults]) => (
            <Grid item xs={2} key={company}>
              <ContentCard elevation={0}>
                <Box display="flex" alignItems="center" marginBottom={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CompanyLogo src={`/logos/${company}.png`} alt={company.charAt(0).toUpperCase() + company.slice(1)} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {company.charAt(0).toUpperCase() + company.slice(1)}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ marginBottom: 2 }} />
                {companyResults.items.map((result, index) => {
                  const isRecent = result.date ? 
                    (new Date().getTime() - new Date(result.date).getTime()) < 30 * 24 * 60 * 60 * 1000 : 
                    false;

                  return (
                    <ResultCard key={`${company}-${index}`}>
                      {result.date && (
                        <ResultDate>
                          <FreshnessIndicator fresh={isRecent} />
                          {result.date}
                          {result.fileType && <FileType label={result.fileType} size="small" />}
                        </ResultDate>
                      )}
                      <StyledLink href={result.url} target="_blank" rel="noopener noreferrer">
                        {result.title || result.url}
                      </StyledLink>
                      {result.snippet && (
                        <SnippetText>
                          {result.snippet}
                        </SnippetText>
                      )}
                    </ResultCard>
                  );
                })}
              </ContentCard>
            </Grid>
          ))}
        </Grid>
      </ResultsContainer>
    </Box>
  );
};

export default CompetitorSearch; 